/**
 * Small display helpers for the SpecLens screen, kept out of the components so node --test can
 * run them without a DOM (the same split state.ts and templatecreator-view.ts make).
 */

import type { TranslationStatus } from "@plan-forge/speclens-contract";

export interface Segment {
  readonly text: string;
  readonly hit: boolean;
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Splits an excerpt into runs, marking the ones that are one of the terms, so the reviewer sees
 * why a clause matched. Case-insensitive, longest term first so "70V speaker" wins over "70V".
 */
export function highlightSegments(text: string, terms: readonly string[]): readonly Segment[] {
  const usable = terms.map((term) => term.trim()).filter((term) => term.length > 0);
  if (text.length === 0 || usable.length === 0) return [{ text, hit: false }];

  const pattern = new RegExp(
    `(${[...usable].sort((a, b) => b.length - a.length).map(escapeRegExp).join("|")})`,
    "gi",
  );
  const segments: Segment[] = [];
  let cursor = 0;
  for (const match of text.matchAll(pattern)) {
    if (match.index > cursor) segments.push({ text: text.slice(cursor, match.index), hit: false });
    segments.push({ text: match[0], hit: true });
    cursor = match.index + match[0].length;
  }
  if (cursor < text.length) segments.push({ text: text.slice(cursor), hit: false });
  return segments;
}

/** A word for the status, so it never rests on colour alone. */
export function statusLabel(status: TranslationStatus): string {
  switch (status) {
    case "equivalent":
      return "Equivalent";
    case "close":
      return "Close - see differences";
    case "none":
      return "No AE equivalent";
    case "unknown":
      return "Needs a catalog row";
  }
}

export const DIVISION_NAMES: Readonly<Record<string, string>> = {
  "00": "Procurement and contracting",
  "01": "General requirements",
  "09": "Finishes",
  "26": "Electrical",
  "27": "Communications",
  "28": "Electronic safety and security",
};
